import { Metamodel } from 'app/model/base/model';
import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { SapHanaLinkedService } from 'app/model/linkedservice/saphana.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { SapHanaCopySource } from 'app/model/pipeline/copysource/saphana.copysource';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const dynamicTextboxControl: ControlMetadata = {
    type: 'parameterizable', child: {
        type: 'textbox',
        bind: 'server',
        label: stringResources.ModelName_ServerName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};

const dynamicUserNameControl: ControlMetadata = {
    type: 'parameterizable', child: {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};


const dynamicPasswordControl: ControlMetadata = {
    enableAKVParameterization: true,
    hideAKVSwitcherInConnStrProps: true,
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'parameterizable',
        attribute: 'password',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template, stringResources.SapHana_DisplayText,
        stringResources.ModelName_Password)
};


const basicControls: ControlMetadata[] = [
    dynamicTextboxControl,
    {
        type: 'select',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        defaultValue: 'Basic',
        isLabelVertical: true,
        options: [
            {
                label: stringResources.AuthenticationType_Select_Basic_Option,
                value: 'Basic'
            },
            {
                label: stringResources.AuthenticationType_Select_Windows_Option,
                value: 'Windows'
            }
        ]
    },
    dynamicUserNameControl,
    dynamicPasswordControl,
    {
        type: 'key-val-list',
        bind: 'additionalFields',
        label: stringResources.AdditionalConnectionStringFields
    }
];

export const sapHanaMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(SapHanaLinkedService).name,
        displayName: stringResources.SapHana_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-SapHANA'
        },
        isOnPremises: true,
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        copySinkTypes: [],
        supportDatasetV2: true,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.SapHanaDatabase
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic, ConnectionAuthKind.Windows],
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportMultipleIncrementalColumns: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        onetimeQueryTemplate: 'SELECT <<column>>\nFROM "_SYS_BIC"."<<Package>>/<<Cube>>"\nGROUP BY <<column>>',
        // eslint-disable-next-line max-len
        timedQueryTemplate: 'SELECT <<column>>\nFROM "_SYS_BIC"."<<Package>>/<<Cube>>"\nWHERE <<column>> > \'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-dd HH:mm\' )}\' \nand <<column>> <\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-dd HH:mm\' )}\'\nGROUP BY <<column>>',
        copySourceTypes: [Metamodel.modelFor(SapHanaCopySource).name],
        category: ['Database'],
        controls: [
            // for !isOldMashupResource
            {
                type: 'select-button-switch',
                required: true,
                isLabelVertical: true,
                options: [
                    {
                        label: stringResources.ModelName_ConnectionString,
                        value: false
                    },
                    {
                        label: stringResources.AzureKeyVault_DisplayText,
                        value: true
                    }
                ],
                bind: 'enableAKV',
                isNotRelatedToCredential: true,
                defaultValue: false,
                hide: `{ isOldMashupResource }`,
                cases: [
                    {
                        caseValue: true,
                        children: [
                            {
                                type: 'azure-key-vault',
                                bind: 'referedAKV',
                                enableAKVParameterization: true,
                                isLabelVertical: true,
                                description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SapHana_DisplayText),
                                    'SERVERNODE=<accountname>;UID=<username>;PWD=<password>')
                            }
                        ]
                    },
                    {
                        caseValue: false,
                        children: basicControls
                    }
                ]
            },
            // for isOldMashupResource
            ...(basicControls.map((item) => ({
                ...item,
                hide: `{ !isOldMashupResource }`
            })))
        ]
    };
    return metadata;
};
